import { EntityType } from '@frontend/common';
import { Application, Feature, FeatureTarget, Functionality, FunctionalityType } from '@frontend/functionality/types';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';

interface PlatformFunctionalityFilterOptions {
    includeHidden?: boolean;
}

export function filterPlatformCorporateFunctionalities(
    functionalities: Functionality[],
    application: Application,
    feature?: Feature
): Functionality[] {
    return filterPlatformFunctionalities(functionalities, application, feature, FunctionalityType.CORPORATE);
}

export function filterPlatformFunctionalities(
    functionalities: Functionality[],
    application: Application,
    feature?: Feature,
    type: FunctionalityType = FunctionalityType.CORPORATE,
    options?: PlatformFunctionalityFilterOptions
): Functionality[] {
    return functionalities
        .filter(
            (f) =>
                f.application === application &&
                f.target === FeatureTarget.PLATFORM &&
                f.type === type &&
                (options?.includeHidden === true || f.hidden !== true) &&
                (feature === undefined || f.feature === feature)
        )
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function findApiFunctionality(
    functionalities: Functionality[],
    application: Application,
    feature: Feature,
    type: FunctionalityType = FunctionalityType.CORPORATE
): Functionality | undefined {
    return functionalities.find(
        (f) =>
            f.application === application &&
            f.feature === feature &&
            f.type === type &&
            f.target === FeatureTarget.API &&
            f.hidden !== true
    );
}

export function findPlatformFunctionality(
    functionalities: Functionality[],
    application: Application,
    feature: Feature,
    type: FunctionalityType = FunctionalityType.CORPORATE,
    options?: PlatformFunctionalityFilterOptions
): Functionality | undefined {
    return filterPlatformFunctionalities(functionalities, application, feature, type, options)[0];
}

export interface FunctionalityWizardContext {
    functionality: Functionality;
    wizardId: string;
}

/**
 * @deprecated
 **/
export async function resolveFirstFunctionalityWithWizardId(functionalities: Functionality[]): Promise<FunctionalityWizardContext | null> {
    for (const functionality of functionalities) {
        const wizardId = await resolveFunctionalityWizardId(functionality);
        if (wizardId) {
            return { functionality, wizardId };
        }
    }
    return null;
}


/**
 * @deprecated
 **/
export async function resolveFunctionalityWizardId(functionality: Functionality): Promise<string | null> {
    if (functionality.wizard_id) {
        return functionality.wizard_id;
    }
    const settings = await SettingClient.fetchSettings({
        entity_id: functionality.id,
        entity_type: EntityType.FUNCTIONALITY
    });
    const setting = settings.results.find((s) => s.key === SettingKey.WIZARD_ID);
    if (setting && typeof setting.value === 'string') {
        return setting.value;
    }
    return null;
}
